class CartShippingProfileProgress extends HTMLElement {
  async connectedCallback() {
    this.#cacheElements();
    this.#renderFromWeight(this.#parseInteger(this.dataset.currentWeightGrams, 0));
    await this.#refreshFromCart();
    document.addEventListener('cart:update', this.#handleCartUpdate);
    document.addEventListener('cart:updated', this.#handleCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.#handleCartUpdate);
    document.removeEventListener('cart:updated', this.#handleCartUpdate);
  }

  #elements;

  #handleCartUpdate = async () => {
    await this.#refreshFromCart();
  };

  #cacheElements() {
    this.#elements = {
      message: this.querySelector('[data-message]'),
      progressBar: this.querySelector('[data-progress-bar]'),
      progressFill: this.querySelector('[data-progress-fill]'),
    };
  }

  async #refreshFromCart() {
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!response.ok) return;
      const cart = await response.json();
      this.#renderFromWeight(this.#getTotalWeightGrams(cart));
    } catch {
      // noop: keep last rendered state
    }
  }

  #getTotalWeightGrams(cart) {
    const totalWeight = this.#parseInteger(cart?.total_weight, 0);
    if (totalWeight > 0) return totalWeight;

    let summedWeight = 0;
    for (const item of cart.items || []) {
      const itemWeight = this.#parseInteger(item?.grams, 0);
      const itemQuantity = this.#parseInteger(item?.quantity, 0);
      summedWeight += itemWeight * itemQuantity;
    }
    return summedWeight;
  }

  #renderFromWeight(totalWeightGrams) {
    const freeShippingWeightGrams = this.#parseInteger(this.dataset.freeShippingWeightGrams, 13118);
    const stageTwoWeightGrams = this.#parseInteger(this.dataset.stageTwoWeightGrams, 6876);
    const normalizedTotalWeightGrams = Math.max(0, this.#parseInteger(totalWeightGrams, 0));

    const isQualified = normalizedTotalWeightGrams > freeShippingWeightGrams;
    let remainingCases = 0;
    let progressPercent = 100;

    if (!isQualified) {
      if (normalizedTotalWeightGrams <= 0) {
        remainingCases = 3;
        progressPercent = 0;
      } else if (normalizedTotalWeightGrams < stageTwoWeightGrams) {
        remainingCases = 2;
        progressPercent = 33;
      } else {
        remainingCases = 1;
        progressPercent = 66;
      }
    }

    this.style.setProperty('--shipping-profile-progress-value', `${progressPercent}%`);

    if (this.#elements.progressBar) {
      this.#elements.progressBar.setAttribute('aria-valuenow', String(progressPercent));
      this.#elements.progressBar.setAttribute('aria-label', this.dataset.progressLabel || 'Progress toward free shipping');
    }

    if (this.#elements.message) {
      if (isQualified) {
        this.#elements.message.textContent = this.dataset.qualifiedMessage || 'Free shipping unlocked';
        return;
      }

      const remainingTemplate = remainingCases === 1
        ? (this.dataset.remainingTemplateSingular || '{count} case away from free shipping!')
        : (this.dataset.remainingTemplate || '{count} cases away from free shipping!');
      this.#elements.message.textContent = remainingTemplate.replace('{count}', String(remainingCases));
    }
  }

  #parseInteger(value, fallback) {
    const parsed = Number.parseInt(value ?? '', 10);
    if (Number.isNaN(parsed)) return fallback;
    return parsed;
  }

}

if (!customElements.get('cart-shipping-profile-progress')) {
  customElements.define('cart-shipping-profile-progress', CartShippingProfileProgress);
}
